// Provider-neutral analytics surface. Feature code uses this:
//
//   analytics.track(ANALYTICS_EVENTS.ORDER_PLACED, { offering_id, amount })
//   analytics.identify(user.id, { email, kyc_status })
//   analytics.reset()                                       // on logout
//
// Today this is Mixpanel-only. If/when a second provider is added (Amplitude,
// GA4), wrap it in the same `AnalyticsClient` shape and fan out from here.
//
// All calls are safe no-ops when the underlying provider isn't initialised
// (token missing in dev/CI, init not yet called).

import { logger } from '@core/logging/logger'
import { mixpanelClient } from './mixpanel'
import type { AnalyticsEvent } from './events'

interface AnalyticsClient {
  init(): void
  identify(userId: string, traits?: Record<string, unknown>): void
  track(event: string, props?: Record<string, unknown>): void
  page(name: string | undefined, props?: Record<string, unknown>): void
  reset(): void
}

const clients: AnalyticsClient[] = [mixpanelClient]

export const analytics = {
  init(): void {
    for (const client of clients) {
      try {
        client.init()
      } catch (error) {
        logger.warn('analytics provider init failed', { error: String(error) })
      }
    }
  },

  identify(userId: string, traits?: Record<string, unknown>): void {
    for (const client of clients) client.identify(userId, traits)
  },

  track(event: AnalyticsEvent, props?: Record<string, unknown>): void {
    for (const client of clients) client.track(event, props)
  },

  page(name?: string, props?: Record<string, unknown>): void {
    for (const client of clients) client.page(name, props)
  },

  reset(): void {
    for (const client of clients) client.reset()
  },
} as const

export { ANALYTICS_EVENTS, type AnalyticsEvent } from './events'
